// `integrations`: Swift `IntegrationConfig` (GroundTeam/Models/CompanySettings.swift). Never readable by reps (the
// server filters them out of a rep's feed). `events` is a free `[String]` in Swift, so event names this app does not
// know survive. Decoding template `IntegrationConfig(kind: .webhook)` (GroundTeam/Sync/ResilientDecoding.swift:137).

import { INTEGRATION_EVENTS, INTEGRATION_KINDS, type IntegrationKind } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  DATE,
  STRING,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface IntegrationPayload extends Passthrough {
  id: string;
  kind: IntegrationKind;
  isEnabled: boolean;
  endpointURL: string;
  /** Pushed events; the known ones are INTEGRATION_EVENTS. */
  events: string[];
  lastSyncAt?: string;
}

export const INTEGRATION_KEYS = [
  'id',
  'kind',
  'isEnabled',
  'endpointURL',
  'events',
  'lastSyncAt',
] as const satisfies readonly (keyof Known<IntegrationPayload>)[];

/** `IntegrationConfig.events` default: all four events. */
const DEFAULT_EVENTS = (): string[] => [...INTEGRATION_EVENTS];

export const INTEGRATION_FIELDS: FieldMap<IntegrationPayload> = {
  id: field.strict(UUID),
  kind: field.req(enumOf('IntegrationKind', INTEGRATION_KINDS), () => 'webhook'),
  isEnabled: field.req(BOOL, () => false),
  endpointURL: field.req(STRING, () => ''),
  events: field.req(arrayOf(STRING), DEFAULT_EVENTS),
  lastSyncAt: field.opt(DATE),
};

export function decodeIntegration(raw: JsonObject, envelopeId?: string): Decoded<IntegrationPayload> {
  return decodeModel(INTEGRATION_FIELDS, raw, {}, envelopeId);
}

/** Swift's memberwise init needs `kind`. */
export type IntegrationInit = Partial<Known<IntegrationPayload>> & { kind: IntegrationKind };

export function newIntegration(init: IntegrationInit): IntegrationPayload {
  return createModel(
    INTEGRATION_FIELDS,
    { id: newId(), isEnabled: false, endpointURL: '', events: DEFAULT_EVENTS() },
    init,
    'integrations',
  );
}
